import React from "react";
import { Testimonial, TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_PEDIATRICS } from "../utils/constants";

type Section = {
  title: string;
  content: string[];
  img?: string;
  ratio?: string;
};

const data: { sections: Section[]; testimonials: Testimonial[] } = {
  sections: [
    {
      title: "Pädiatrie",
      content: [
        "Aufmerksamkeit- und Konzentrationsstörungen",
        "ADS,ADHS",
        "Verhaltensschwierigkeiten z.B. sozialer Rückzug, aggressives Verhalten",
        "Schwierigkeiten mit dem Selbstbewusstsein/Selbstwert",
        "Ängste",
        "Schwierigkeiten im schulischen Bereich",
        "Konfliktsituationen zu Hause oder/und in der Schule",
      ],
      img: process.env.PUBLIC_URL + "/images/paediatrics.jpg",
      ratio: "aspect-[3/4]",
    },
    {
      title: "Was könnte eine Behandlung beinhalten?",
      content: [
        "Erstellen von Tages-/Wochenplänen",
        "Aufbau des Selbstbewusstseins/Selbstvertrauens",
        "Kommunikationstraining",
        "Soziales Kompetenztraining",
        "Erarbeiten von Interessen/Hobbys",
        "Verbesserung der Selbst- und Fremdwahrnehmung",
        "Verbesserung der Körperwahrnehmung",
        "Stressmanagement",
        "Verbesserung der Motivation",
        "Verbesserung der sozio-emotionalen Fähigkeiten",
        "Verbesserung von kognitiven Funktionen (z.B. Konzentrations- und Aufmerksamkeitstraining)",
        "Umgang mit Konfliktsituationen erlernen",
        "Umgang mit Ängsten",
      ],
    },
  ],
  testimonials: [
    {
      quote:
        "Meine Tochter hat im Verlauf der Ergotherapie gelernt, sich Ängsten zu stellen. Sie ist heute in der Lage alleine einkaufen zu gehen, ohne in Panik aus dem Laden zu rennen.",
    },
    {
      quote:
        "Durch die Ergotherapie habe ich viele wertvolle Tipps erhalten, um die Hausaufgaben mit meinem Sohn besser bewältigen zu können.",
    },
  ],
};

export default function PediatricsPage() {
  return (
    <>
      <SEOHelmet page="paediatrie" pathname={PATH_PEDIATRICS} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "description": "Kindgerechte Ergotherapie für ADHS, Konzentrationsstörungen, Entwicklungsverzögerungen und Lernstörungen.",
          "url": "https://ergorw.de/paediatrie",
          "telephone": "+49-741-89698293",
          "email": "info@ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "addressRegion": "Baden-Württemberg",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.1684",
            "longitude": "8.6278"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "07:00",
              "closes": "17:00"
            }
          ],
          "founder": {
            "@type": "Person",
            "name": "Lisa Spreitzer",
            "jobTitle": "Ergotherapeutin"
          },
          "medicalSpecialty": "Pediatric",
          "areaServed": {
            "@type": "City",
            "name": "Rottweil"
          },
          "sameAs": [
            "https://www.instagram.com/ergotherapie_zeittraeume/"
          ]
        })}
      </script>

      <main className="space-y-14">
        {data.sections.map((section, index) => (
          <article key={index}>
            {index === 0 ? (
              // First section with image
              <section className="grid lg:grid-cols-2 gap-12 items-start" aria-labelledby="main-heading">
                <div>
                  <h1
                    id="main-heading"
                    className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
                  >
                    Pädiatrische Ergotherapie
                  </h1>
                  <h2 className="sr-only">Behandlungsschwerpunkte</h2>
                  <ul
                    className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                    aria-label="Behandlungsschwerpunkte in der Pädiatrie"
                  >
                    {section.content.map((item, index) => (
                      <li key={index} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {section.img && (
                  <aside
                    className=""
                    aria-label="Bildliche Darstellung der pädiatrischen Ergotherapie"
                  >
                    <img
                      src={section.img}
                      alt="Pädiatrische Ergotherapie Rottweil - Kindgerechte Behandlung bei Entwicklungsstörungen, ADHS und Verhaltensschwächen"
                      className="w-64 md:w-96 lg:w-124 rounded-2xl object-cover shadow-sm max-h-[600px] aspect-[3/4]"
                    />
                  </aside>
                )}
              </section>
            ) : (
              // Treatment details section
              <section aria-labelledby={`section-${index}`}>
                <h2
                  id={`section-${index}`}
                  className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                >
                  {section.title}
                </h2>
                <ul
                  className="list-disc marker:text-ergo-400 dark:marker:text-ergo-500 list-inside space-y-3 mb-8 text-base text-gray-500 dark:text-gray-400"
                  aria-label="Behandlungsinhalte und Therapiemöglichkeiten"
                >
                  {section.content.map((item, index) => (
                    <li key={index} className="text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        ))}

        {/* Testimonials Section */}
        <section aria-label="Erfahrungsberichte">
          <h2 className="sr-only">Erfahrungsberichte unserer Klienten</h2>
          <TestimonialsComponent testimonials={data.testimonials} />
        </section>
      </main>
    </>
  );
}
